import { ChevronRight } from "lucide-react";
import { Fragment, useState } from "react";
import { formatCellValue } from "@/components/contracts/table-utils";
import { DataTable } from "@/components/shared/data-table";
import type { PrivateHireDriver, PrivateHireVehicle } from "@/lib/coach-manager/types";
import { cn } from "@/lib/utils";

function DriversTable({ drivers }: { drivers: PrivateHireDriver[] }) {
  return (
    <DataTable
      headers={[{ label: "Driver ID" }, { label: "Driver Type" }, { label: "Driver ID (Ext)" }]}
      className="border-none"
    >
      {drivers.map((driver) => (
        <tr key={driver.PrivateHireDriverID} className="border-b last:border-b-0">
          <td className="px-4 py-2">{driver.PrivateHireDriverID}</td>
          <td className="px-4 py-2">{formatCellValue(driver.DriverType)}</td>
          <td className="px-4 py-2">{formatCellValue(driver.DriverID)}</td>
        </tr>
      ))}
    </DataTable>
  );
}

/**
 * Vehicles table where each row expands to reveal that vehicle's own
 * `PrivateHireDrivers[]` beneath it — mirrors `VehiclesTable`. Unlike
 * Contract's vehicle, a `PrivateHireVehicle` also carries its own
 * pricing/VAT/NominalCode fields, shown as extra columns.
 */
export function PrivateHireVehiclesTable({ vehicles }: { vehicles: PrivateHireVehicle[] }) {
  const [expandedId, setExpandedId] = useState<number | null>(null);

  if (vehicles.length === 0) return null;

  return (
    <DataTable
      headers={[
        { label: "", className: "w-8" },
        { label: "Vehicle ID" },
        { label: "Vehicle Type" },
        { label: "Seats" },
        { label: "Price" },
        { label: "VAT Code" },
        { label: "VAT %" },
        { label: "VAT" },
        { label: "Total" },
        { label: "Nominal Code" },
      ]}
    >
      {vehicles.map((vehicle) => {
        const isOpen = expandedId === vehicle.PrivateHireVehicleID;
        const drivers = vehicle.PrivateHireDrivers ?? [];
        return (
          <Fragment key={vehicle.PrivateHireVehicleID}>
            <tr
              className={cn(
                "border-b last:border-b-0",
                drivers.length > 0 && "cursor-pointer hover:bg-muted/40",
              )}
              onClick={
                drivers.length > 0
                  ? () => setExpandedId(isOpen ? null : vehicle.PrivateHireVehicleID)
                  : undefined
              }
            >
              <td className="px-2 py-2">
                {drivers.length > 0 && (
                  <ChevronRight
                    className={cn("h-4 w-4 transition-transform", isOpen && "rotate-90")}
                  />
                )}
              </td>
              <td className="px-4 py-2">{vehicle.PrivateHireVehicleID}</td>
              <td className="px-4 py-2">{formatCellValue(vehicle.VehicleType)}</td>
              <td className="px-4 py-2">{vehicle.Seats}</td>
              <td className="px-4 py-2">{vehicle.Price}</td>
              <td className="px-4 py-2">{vehicle.VATCode}</td>
              <td className="px-4 py-2">{vehicle.VATPercentage}</td>
              <td className="px-4 py-2">{vehicle.VAT}</td>
              <td className="px-4 py-2">{vehicle.Total}</td>
              <td className="px-4 py-2">{formatCellValue(vehicle.NominalCode)}</td>
            </tr>
            {isOpen && drivers.length > 0 && (
              <tr className="border-b bg-muted/20 last:border-b-0">
                <td colSpan={10} className="px-4 py-2">
                  <DriversTable drivers={drivers} />
                </td>
              </tr>
            )}
          </Fragment>
        );
      })}
    </DataTable>
  );
}
